// ────────────────────────────────────────────────────────────────
// Service API — Wrapper fetch centralisé pour FinanceApp
// Gère le token JWT, les erreurs HTTP et la structure des endpoints
// ────────────────────────────────────────────────────────────────

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const TOKEN_KEY = 'financeapp_token';

export const getToken    = ()  => localStorage.getItem(TOKEN_KEY);
export const setToken    = (t) => localStorage.setItem(TOKEN_KEY, t);
export const removeToken = ()  => localStorage.removeItem(TOKEN_KEY);

// Requête HTTP générique avec gestion JWT et erreurs
async function req(method, path, body) {
  const token = getToken();
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);

  let res;
  try {
    res = await fetch(`${BASE}${path}`, opts);
  } catch {
    throw new Error('Impossible de contacter le serveur. Vérifiez que le backend tourne sur le port 3001.');
  }

  // Session expirée → nettoyage et reload
  if (res.status === 401) {
    removeToken();
    window.dispatchEvent(new Event('financeapp:logout'));
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Code et détails transmis à l'écran (ex. doublon probable → bouton
    // « C'est une facture distincte »), en plus du message
    const err = new Error(data.error || `Erreur ${res.status}`);
    err.code = data.code;
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Requête binaire (PDF) avec JWT — retourne un Blob
async function reqBlob(path) {
  const token = getToken();
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'GET',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
  } catch {
    throw new Error('Impossible de contacter le serveur.');
  }
  if (res.status === 401) {
    removeToken();
    window.dispatchEvent(new Event('financeapp:logout'));
    throw new Error('Session expirée.');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Erreur ${res.status}`);
  }
  return res.blob();
}

// Requête binaire nommée (fichier à télécharger) avec JWT — retourne { blob, filename }
// À utiliser quand le backend fixe le nom de fichier via Content-Disposition
// (ex: exports FEC, PDF avec nom métier). Ne remplace PAS reqBlob, qui reste
// utilisé tel quel par documents.invoicePdf / documents.blPdf.
async function reqBlobFile(path, body) {
  const token = getToken();
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { ...(token && { Authorization: `Bearer ${token}` }), ...(body && { 'Content-Type': 'application/json' }) },
      ...(body && { body: JSON.stringify(body) }),
    });
  } catch {
    throw new Error('Impossible de contacter le serveur.');
  }
  if (res.status === 401) {
    removeToken();
    window.dispatchEvent(new Event('financeapp:logout'));
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Erreur ${res.status}`);
  }
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match ? match[1] : 'export.txt';
  const blob = await res.blob();
  return { blob, filename };
}

// Requête multipart (upload de fichier) avec JWT — ne PAS fixer Content-Type,
// le navigateur pose lui-même le boundary multipart correct.
async function reqUpload(path, formData, method = 'POST') {
  const token = getToken();
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
  } catch {
    throw new Error('Impossible de contacter le serveur.');
  }
  if (res.status === 401) {
    removeToken();
    window.dispatchEvent(new Event('financeapp:logout'));
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Réponses sans message JSON (refus du proxy nginx avant l'application) :
    // message clair plutôt que « Erreur 413 ».
    const PROXY_MESSAGES = {
      413: 'Fichier trop volumineux (15 Mo maximum). Compressez le PDF ou scannez-le en résolution plus faible.',
      502: 'Le serveur est momentanément indisponible. Réessayez dans un instant.',
      504: 'Le traitement du fichier a pris trop de temps. Réessayez ; si cela se reproduit, découpez le PDF.',
    };
    const err = new Error(data.error || PROXY_MESSAGES[res.status] || `Erreur ${res.status}`);
    err.code = data.code;
    err.status = res.status;
    // Délai avant nouvel essai sur limite atteinte (secondes), si fourni
    const wait = Number(res.headers?.get?.('Retry-After') || res.headers?.get?.('RateLimit-Reset'));
    if (wait > 0) err.retryAfter = wait;
    throw err;
  }
  return data;
}

// Helper pour construire une query string proprement
function qs(params = {}) {
  const p = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''));
  return Object.keys(p).length ? '?' + new URLSearchParams(p) : '';
}

// ── API structurée par ressource ──────────────────────────────
const api = {
  // Fichier protégé désigné par une URL d'API (exportUrl, templateUrl…) :
  // récupéré AVEC le jeton. Un <a href={url}> direct ne l'envoie pas →
  // « Token d'authentification manquant ». À utiliser avec downloadProtectedFile.
  fetchFile: (url) => reqBlobFile(url.startsWith(BASE) ? url.slice(BASE.length) : url),

  // ── Authentification ───────────────────────────────────────
  auth: {
    login:      (email, password, totpCode, remember_me) => req('POST', '/auth/login', { email, password, totpCode, remember_me }),
    me:         ()      => req('GET',  '/auth/me'),
    register:   (data)  => req('POST', '/auth/register', data),
    registrationStatus: () => req('GET', '/auth/registration-status'),
    setup2FA:   ()      => req('POST', '/auth/2fa/setup'),
    verify2FA:  (token) => req('POST', '/auth/2fa/verify', { token }),
    validate2FA:(token) => req('POST', '/auth/2fa/validate', { token }),
    disable2FA: (pass)  => req('POST', '/auth/2fa/disable', { password: pass }),
    switchCompany: (company_id) => req('POST', '/auth/switch-company', { company_id }),
    forgotPassword: (email)    => req('POST', '/auth/forgot-password', { email }),
    resetPassword:  (token, password) => req('POST', `/auth/reset-password/${token}`, { password }),
  },

  // ── Sociétés ────────────────────────────────────────────────
  companies: {
    list:   ()           => req('GET',    '/companies'),
    get:    (id)         => req('GET',    `/companies/${id}`),
    stats:  (id)         => req('GET',    `/companies/${id}/stats`),
    create: (data)       => req('POST',   '/companies', data),
    update: (id, data)   => req('PUT',    `/companies/${id}`, data),
    delete: (id)         => req('DELETE', `/companies/${id}`),
  },

  // ── Factures / Devis / Avoirs ─────────────────────────────────
  invoices: {
    list:        (p = {})     => req('GET',    `/invoices${qs(p)}`),
    stats:       (p = {})     => req('GET',    `/invoices/stats${qs(p)}`),
    // À encaisser, « Déjà encaissée — à rapprocher », encaissements en attente (Q-clients)
    receivables: (view)       => req('GET',    `/invoices/receivables${qs({ view })}`),
    pdfInfo:     (kind, id)   => req('GET',    `/invoice-pdf/${kind}/${id}/info`), // 📎 vente | achat (A1b)
    pdfFile:     (kind, id)   => reqBlob(`/invoice-pdf/${kind}/${id}/file`),
    settledMark: (ids, marked) => req('POST',  '/invoices/settled-mark', { ids, marked }),
    inflowsToValidate: ()     => req('GET',    '/invoices/inflows-to-validate'),
    get:         (id)         => req('GET',    `/invoices/${id}`),
    create:      (data)       => req('POST',   '/invoices', data),
    update:      (id, data)   => req('PUT',    `/invoices/${id}`, data),
    status:      (id, status, extra = {}) => req('PATCH',  `/invoices/${id}/status`, { status, ...extra }),
    delete:      (id)         => req('DELETE', `/invoices/${id}`),
    convert:     (id)         => req('POST',   `/invoices/${id}/convert`, {}),
    avoir:       (id)         => req('POST',   `/invoices/${id}/avoir`, {}),
    exportCsvDownload: (companyId) => reqBlobFile(`/invoices/export/csv${qs({ company_id: companyId })}`),
    send:        (id)         => req('POST',   `/factelec/invoices/${id}/send`, {}),
    pdfUrl:      (id, lang='fr', type='facture') => `${BASE}/documents/invoice/${id}/pdf?lang=${lang}&type=${type}`,
  },

  // ── Bons de livraison ─────────────────────────────────────────
  deliveryNotes: {
    list:            (cid, p={})  => req('GET',    `/delivery-notes${qs({ company_id: cid, ...p })}`),
    get:             (id)         => req('GET',    `/delivery-notes/${id}`),
    create:          (data)       => req('POST',   '/delivery-notes', data),
    update:          (id, data)   => req('PUT',    `/delivery-notes/${id}`, data),
    status:          (id, status) => req('PATCH',  `/delivery-notes/${id}/status`, { status }),
    delete:          (id)         => req('DELETE', `/delivery-notes/${id}`),
    generateInvoice: (id)         => req('POST',   `/delivery-notes/${id}/generate-invoice`, {}),
    pdfUrl:          (id)         => `${BASE}/delivery-notes/${id}/pdf`,
    pdfDownload:     (id)         => reqBlobFile(`/delivery-notes/${id}/pdf`),
  },

  // ── Comptabilité analytique ────────────────────────────────
  analytics: {
    // Axes
    axes:       (cid)        => req('GET',    `/analytics/axes${qs({ company_id: cid })}`),
    createAxis: (data)       => req('POST',   '/analytics/axes', data),
    updateAxis: (id, data)   => req('PUT',    `/analytics/axes/${id}`, data),
    deleteAxis: (id)         => req('DELETE', `/analytics/axes/${id}`),
    // Codes
    codes:       (p = {})    => req('GET',    `/analytics/codes${qs(p)}`),
    createCode:  (data)      => req('POST',   '/analytics/codes', data),
    updateCode:  (id, data)  => req('PUT',    `/analytics/codes/${id}`, data),
    deleteCode:  (id)        => req('DELETE', `/analytics/codes/${id}`),
    // Entrées
    entries:     (p = {})    => req('GET',    `/analytics/entries${qs(p)}`),
    createEntries:(data)     => req('POST',   '/analytics/entries', data),
    deleteEntry: (id)        => req('DELETE', `/analytics/entries/${id}`),
    deleteDocEntries:(t, id) => req('DELETE', `/analytics/entries/document/${t}/${id}`),
    // Reports
    reportPL:       (p = {}) => req('GET', `/analytics/report/pl${qs(p)}`),
    reportComparison:(p= {}) => req('GET', `/analytics/report/comparison${qs(p)}`),
    reportBudget:   (p = {}) => req('GET', `/analytics/report/budget-vs-actual${qs(p)}`),
    reportEvolution:(p = {}) => req('GET', `/analytics/report/evolution${qs(p)}`),
    // Dashboard & budgets
    dashboard:   (cid)       => req('GET',  `/analytics/dashboard${qs({ company_id: cid })}`),
    budgets:     (p = {})    => req('GET',  `/analytics/budgets${qs(p)}`),
    saveBudgets: (data)      => req('POST', '/analytics/budgets', data),
    // Affectation
    unaffected:  (cid)       => req('GET',  `/analytics/unaffected${qs({ company_id: cid })}`),
    assign:      (data)      => req('POST', '/analytics/unaffected/assign', data),
    // Règles
    rules:       (cid)       => req('GET',    `/analytics/rules${qs({ company_id: cid })}`),
    createRule:  (data)      => req('POST',   '/analytics/rules', data),
    deleteRule:  (id)        => req('DELETE', `/analytics/rules/${id}`),
    // Init
    init:        (data)      => req('POST',   '/analytics/init', data),
  },

  // ── Dashboard ────────────────────────────────────────────────
  dashboard: {
    kpis:    (cid)  => req('GET', `/dashboard${qs({ company_id: cid })}`),
    chart:   (cid)  => req('GET', `/dashboard/chart/${cid}`),
    group:   ()     => req('GET', '/dashboard/group'),
    urgent:  (cid)  => req('GET', `/dashboard/urgent-payments${qs({ company_id: cid })}`),
    yoyComparison: (cid)       => req('GET',  `/dashboard/yoy-comparison${qs({ company_id: cid })}`),
    manualKpi:     (cid)       => req('GET',  `/dashboard/manual-kpi/${cid}`),
    saveManualKpi: (cid, data) => req('POST', `/dashboard/manual-kpi/${cid}`, data),
  },

  // ── Comptes bancaires ───────────────────────────────────────
  bankAccounts: {
    list:         (cid)        => req('GET',    `/bank-accounts${qs({ company_id: cid })}`),
    create:       (data)       => req('POST',   '/bank-accounts', data),
    update:       (id, data)   => req('PUT',    `/bank-accounts/${id}`, data),
    delete:       (id)         => req('DELETE', `/bank-accounts/${id}`),
    transactions: (id, p = {}) => req('GET',    `/bank-accounts/${id}/transactions${qs(p)}`),
    sync:         (id)         => req('PATCH',  `/bank-accounts/${id}/sync`, {}),
  },

  // ── Bridge Open Banking (simulation) ─────────────────────────
  bank: {
    banks:   ()        => req('GET',  '/bank/banks'),
    status:  (cid)     => req('GET',  `/bank/status${qs({ company_id: cid })}`),
    connect: (data)    => req('POST', '/bank/connect', data),
    transactions: (p)  => req('GET',  `/bank/transactions${qs(p)}`),
    sync:    (aid)     => req('POST', `/bank/sync/${aid}`),
  },

  // ── Notes de frais ───────────────────────────────────────────
  // Notes de frais comptabilisées : création / modification en multipart (justificatif « receipt »)
  // ── Aide : nouveautés et guides d'utilisation (23/10/2026) ────
  help: {
    changelog:     ()   => req('GET',  '/help/changelog'),
    changelogSeen: ()   => req('POST', '/help/changelog/seen', {}),
    guides:        ()   => req('GET',  '/help/guides'),
    guide:         (id) => req('GET',  `/help/guides/${encodeURIComponent(id)}`),
  },

  expenses: {
    list:         (p = {})     => req('GET',    `/expenses${qs(p)}`),
    stats:        (cid)        => req('GET',    `/expenses/stats/${cid}`),
    categories:   ()           => req('GET',    '/expenses/categories'),
    settings:     (cid)        => req('GET',    `/expenses/settings/${cid}`),
    saveSettings: (cid, data)  => req('PUT',    `/expenses/settings/${cid}`, data),
    createBeneficiary: (data)   => req('POST',   '/expenses/beneficiaries', data),
    updateBeneficiary: (id, data) => req('PUT',  `/expenses/beneficiaries/${id}`, data),
    createAccount: (data)       => req('POST',   '/expenses/accounts', data),
    get:          (id)         => req('GET',    `/expenses/${id}`),
    create:       (fd)         => reqUpload('/expenses', fd),
    update:       (id, fd)     => reqUpload(`/expenses/${id}`, fd, 'PUT'),
    submit:       (id)         => req('POST',   `/expenses/${id}/submit`, {}),
    validate:     (id, data)   => req('POST',   `/expenses/${id}/validate`, data),
    reject:       (id, reason) => req('POST',   `/expenses/${id}/reject`, { reason }),
    cancel:       (id)         => req('POST',   `/expenses/${id}/cancel`, {}),
    receipt:      (id)         => reqBlob(`/expenses/${id}/receipt`),
    candidates:   (cid, ids)   => req('GET',    `/expenses/reimbursement/candidates${qs({ company_id: cid, ids: ids.join(',') })}`),
    reimburse:    (cid, ids, stagingId) => req('POST', '/expenses/reimburse', { company_id: cid, expense_ids: ids, staging_id: stagingId }),
    reimbursePreview: (cid, ids, stagingId) => req('POST', '/expenses/reimburse/preview', { company_id: cid, expense_ids: ids, staging_id: stagingId }),
    reimburseSuggestions: (cid) => req('GET',    `/expenses/reimbursement/suggestions${qs({ company_id: cid })}`),
    reimburseLine: (stagingId)  => req('GET',    `/expenses/reimbursement/line/${stagingId}`),
    reimburseCancel: (stagingId) => req('POST',  `/expenses/reimbursement/${stagingId}/cancel`, {}),
    delete:       (id)         => req('DELETE', `/expenses/${id}`),
  },

  // ── Commissions ──────────────────────────────────────────────
  commissions: {
    list:          (p = {})     => req('GET',    `/commissions${qs(p)}`),
    stats:         (cid)        => req('GET',    `/commissions/stats/${cid}`),
    create:        (data)       => req('POST',   '/commissions', data),
    update:        (id, data)   => req('PUT',    `/commissions/${id}`, data),
    status:        (id, status) => req('PATCH',  `/commissions/${id}/status`, { status }),
    delete:        (id)         => req('DELETE', `/commissions/${id}`),
    // Commerciaux
    reps:          ()           => req('GET',    '/commissions/reps/list'),
    createRep:     (data)       => req('POST',   '/commissions/reps', data),
    updateRep:     (id, data)   => req('PUT',    `/commissions/reps/${id}`, data),
    deleteRep:     (id)         => req('DELETE', `/commissions/reps/${id}`),
    repAccounts:   (id)         => req('GET',    `/commissions/reps/${id}/accounts`),
    assign:        (id, data)   => req('POST',   `/commissions/reps/${id}/assign`, data),
    unassign:      (id, cid)    => req('DELETE', `/commissions/reps/${id}/assign/${cid}`),
    repDashboard:  (id, p = {}) => req('GET',    `/commissions/reps/${id}/dashboard${qs(p)}`),
    // Rapport & calcul
    report:        (p = {})     => req('GET',    `/commissions/report${qs(p)}`),
    calculate:     (data)       => req('POST',   '/commissions/calculate', data),
    validate:      (id)         => req('POST',   `/commissions/validate/${id}`, {}),
    pay:           (data)       => req('POST',   '/commissions/pay', data),
    paymentHistory:(p = {})     => req('GET',    `/commissions/payments${qs(p)}`),
    // Règles multi-niveaux
    repRules:      (repId)      => req('GET',    `/commissions/reps/${repId}/rules`),
    createRule:    (repId, data)=> req('POST',   `/commissions/reps/${repId}/rules`, data),
    updateRule:    (id, data)   => req('PUT',    `/commissions/rules/${id}`, data),
    deleteRule:    (id)         => req('DELETE', `/commissions/rules/${id}`),
    logs:          (p = {})     => req('GET',    `/commissions/logs${qs(p)}`),
  },

  // ── Chatbot IA ────────────────────────────────────────────────
  chatbot: {
    message:       (data)  => req('POST', '/chatbot/message', data),
    lead:          (data)  => req('POST', '/chatbot/lead', data),
    conversations: (p={})  => req('GET',  `/chatbot/conversations${qs(p)}`),
    stats:         ()      => req('GET',  '/chatbot/stats'),
    conversation:  (sid)   => req('GET',  `/chatbot/conversation/${sid}`),
  },

  // ── Portail Client ────────────────────────────────────────────
  portal: {
    sendInvitation: (data)       => req('POST',   '/portal/send-invitation', data),
    stats:          ()           => req('GET',    '/portal/stats'),
    accesses:       (cid)        => req('GET',    `/portal/accesses${qs({ company_id: cid })}`),
    revokeAccess:   (id)         => req('DELETE', `/portal/accesses/${id}`),
  },

  // ── Accès experts-comptables ─────────────────────────────────
  externalAccess: {
    list:   (cid)        => req('GET',    `/external-access${qs({ company_id: cid })}`),
    get:    (id)         => req('GET',    `/external-access/${id}`),
    create: (data)       => req('POST',   '/external-access', data),
    update: (id, data)   => req('PUT',    `/external-access/${id}`, data),
    revoke: (id)         => req('PATCH',  `/external-access/${id}/revoke`),
    delete: (id)         => req('DELETE', `/external-access/${id}`),
    verify: (token)      => req('POST',   '/external-access/verify', { token }),
  },

  // ── FEC ──────────────────────────────────────────────────────
  fec: {
    export:         (cid, year) => req('GET',  `/fec/export/${cid}${qs({ year })}`),
    exportDownload: (cid, year) => reqBlobFile(`/fec/export/${cid}${qs({ year })}`),
    // Plage de dates arbitraire (ex: export prospect avant archivage — cf.
    // TrialBanner.jsx) plutôt qu'une année civile.
    exportRangeDownload: (cid, dateFrom, dateTo) =>
      reqBlobFile(`/fec/export/${cid}${qs({ date_from: dateFrom, date_to: dateTo })}`),
    import:   (cid, content) => req('POST', `/fec/import/${cid}`, { content }),
    validate: (cid, year) => req('GET',  `/fec/validate/${cid}${qs({ year })}`),
  },

  // ── Journal d'audit ─────────────────────────────────────────────
  audit: {
    list:  (p = {}) => req('GET', `/audit${qs(p)}`),
    stats: (p = {}) => req('GET', `/audit/stats${qs(p)}`),
  },

  // ── Relances ─────────────────────────────────────────────────
  relances: {
    list:   (p = {})     => req('GET',    `/relances${qs(p)}`),
    auto:   (cid)        => req('POST',   `/relances/auto/${cid}`),
    create: (data)       => req('POST',   '/relances', data),
    send:   (id)         => req('PATCH',  `/relances/${id}/send`),
    status: (id, status) => req('PATCH',  `/relances/${id}/status`, { status }),
    delete: (id)         => req('DELETE', `/relances/${id}`),
  },

  // ── Gestion des stocks ────────────────────────────────────────
  stocks: {
    dashboard:      ()               => req('GET',    '/stocks/dashboard'),
    products:       (cid, p = {})    => req('GET',    `/stocks/products${qs(p)}`),
    productById:    (id)             => req('GET',    `/stocks/products/${id}`),
    productMvts:    (id, p = {})     => req('GET',    `/stocks/products/${id}/movements${qs(p)}`),
    productBatches: (id)             => req('GET',    `/stocks/products/${id}/batches`),
    productByEan:   (ean)            => req('GET',    `/stocks/products/ean/${ean}`),
    productAlerts:  ()               => req('GET',    '/stocks/products/alerts'),
    createProduct:  (data)           => req('POST',   '/stocks/products', data),
    importProducts: (data)           => req('POST',   '/stocks/products/import', data),
    updateProduct:  (id, data)       => req('PUT',    `/stocks/products/${id}`, data),
    deleteProduct:  (id)             => req('DELETE', `/stocks/products/${id}`),
    deleteProductPermanent: (id)     => req('DELETE', `/stocks/products/${id}/permanent`),
    movements:      (p = {})         => req('GET',    `/stocks/movements${qs(p)}`),
    entry:          (data)           => req('POST',   '/stocks/movements/entry', data),
    exit:           (data)           => req('POST',   '/stocks/movements/exit', data),
    adjustment:     (data)           => req('POST',   '/stocks/movements/adjustment', data),
    return:         (data)           => req('POST',   '/stocks/movements/return', data),
    transfer:       (data)           => req('POST',   '/stocks/movements/transfer', data),
    valuation:      (cid)            => req('GET',    `/stocks/valorisation/fifo${qs({ company_id: cid })}`),
    valuationSum:   (cid)            => req('GET',    `/stocks/valorisation/summary${qs({ company_id: cid })}`),
    inventory:      (cid, data)      => req('POST',   `/stocks/inventory/${cid}`, data),
    inventoryHist:  (cid)            => req('GET',    `/stocks/inventory/${cid}/history`),
    suppliers:      (cid)            => req('GET',    `/stocks/suppliers${qs({ company_id: cid })}`),
    supplierById:   (id, cid)        => req('GET',    `/stocks/suppliers/${id}${qs({ company_id: cid })}`),
    createSupplier: (data)           => req('POST',   '/stocks/suppliers', data),
    updateSupplier: (id, data)       => req('PUT',    `/stocks/suppliers/${id}`, data),
    deleteSupplier: (id)             => req('DELETE', `/stocks/suppliers/${id}`),
    orders:         (cid, p = {})    => req('GET',    `/stocks/orders${qs({ company_id: cid, ...p })}`),
    orderById:      (id)             => req('GET',    `/stocks/orders/${id}`),
    orderSuggests:  (cid)            => req('GET',    `/stocks/orders/suggestions${qs({ company_id: cid })}`),
    createOrder:    (data)           => req('POST',   '/stocks/orders', data),
    updateOrder:    (id, data)       => req('PUT',    `/stocks/orders/${id}`, data),
    orderStatus:    (id, status)     => req('PATCH',  `/stocks/orders/${id}/status`, { status }),
    receiveOrder:   (id, data)       => req('POST',   `/stocks/orders/${id}/receive`, data),
    cancelOrder:    (id)             => req('DELETE', `/stocks/orders/${id}`),
    warehouses:     (cid)            => req('GET',    `/stocks/warehouses${qs({ company_id: cid })}`),
    createWarehouse:(data)           => req('POST',   '/stocks/warehouses', data),
    updateWarehouse:(id, data)       => req('PUT',    `/stocks/warehouses/${id}`, data),
    exportProducts:      (cid)        => `${BASE}/stocks/export/products?company_id=${cid}`,
    exportMvts:          (cid)        => `${BASE}/stocks/export/movements?company_id=${cid}`,
    interCompanyTransfer:(data)       => req('POST',  '/stocks/movements/inter-company-transfer', data),
    interCompanyTransfers:(cid, p={}) => req('GET',   `/stocks/inter-company-transfers${qs({ company_id: cid, ...p })}`),
    receiveInterTransfer:(id, data)   => req('PATCH', `/stocks/inter-company-transfers/${id}/receive`, data),
    uploadProductImage:  (id, data)   => req('POST',  `/stocks/products/${id}/image`, data),
    preparations:        (cid, p={})  => req('GET',   `/stocks/preparations${qs({ company_id: cid, ...p })}`),
    preparationById:     (id)         => req('GET',   `/stocks/preparations/${id}`),
    createPreparation:   (data)       => req('POST',  '/stocks/preparations', data),
    preparationStatus:   (id, data)   => req('PATCH', `/stocks/preparations/${id}/status`, data),
    cancelPreparation:   (id)         => req('PATCH', `/stocks/preparations/${id}/cancel`, {}),
    pickLine:            (prepId, lineId, data) => req('PUT', `/stocks/preparations/${prepId}/picking/${lineId}`, data),
    shippingLabel:       (id)         => `${BASE.replace('/api','')}/api/stocks/preparations/${id}/shipping-label`,
    productImgUrl:       (path)       => path ? `${BASE.replace('/api','')}${path}` : null,
  },

  // ── Expédition ───────────────────────────────────────────────────
  shipping: {
    carriers:     ()       => req('GET',  '/shipping/carriers'),
    rates:        (p = {}) => req('GET',  `/shipping/rates${qs(p)}`),
    createLabel:  (data)   => req('POST', '/shipping/create-label', data),
    track:        (num)    => req('GET',  `/shipping/track/${num}`),
  },

  // ── Transactions comptables ───────────────────────────────────
  transactions: {
    list:        (p = {})   => req('GET',    `/transactions${qs(p)}`),
    balance:     (cid)      => req('GET',    `/transactions/balance/${cid}`),
    grandLivre:  (p = {})   => req('GET',    `/transactions/grand-livre${qs(p)}`),
    balanceAgee: ()         => req('GET',    '/transactions/balance-agee'),
    // Saisie, modification et suppression à la main : désactivées côté serveur (26/10/2026)
  },

  // ── Périodes comptables ───────────────────────────────────────
  periods: {
    list:                (p = {})    => req('GET',  `/periods${qs(p)}`),
    create:              (data)      => req('POST', '/periods', data),
    close:               (id)        => req('POST', `/periods/${id}/close`, {}),
    lock:                (id)        => req('POST', `/periods/${id}/lock`, {}),
    unlock:              (id, data)  => req('POST', `/periods/${id}/unlock`, data),
    balanceCheck:        (id)        => req('GET',  `/periods/${id}/balance-check`),
    generateOpeningEntries: (data)   => req('POST', '/periods/generate-opening-entries', data),
    auditLog:            (id)        => req('GET',  `/periods/${id}/audit-log`),
  },

  // ── Provisions & Régularisations ─────────────────────────────
  accruals: {
    list:    (p = {})   => req('GET',    `/accruals${qs(p)}`),
    get:     (id)       => req('GET',    `/accruals/${id}`),
    create:  (data)     => req('POST',   '/accruals', data),
    reverse: (id, data) => req('POST',   `/accruals/${id}/reverse`, data || {}),
    cancel:  (id)       => req('DELETE', `/accruals/${id}`),
  },

  // ── Lettrage ──────────────────────────────────────────────────
  lettrage: {
    list:      (p = {}) => req('GET',    `/lettrage${qs(p)}`),
    unmatched: (p = {}) => req('GET',    `/lettrage/unmatched${qs(p)}`),
    create:    (data)   => req('POST',   '/lettrage', data),
    remove:    (id)     => req('DELETE', `/lettrage/${id}`),
  },

  // ── Clients ───────────────────────────────────────────────────
  customers: {
    list:   (cid, search, opts = {}) => req('GET', `/customers${qs({ company_id: cid, search, ...opts })}`),
    get:    (id)          => req('GET',    `/customers/${id}`),
    create: (data)        => req('POST',   '/customers', data),
    update: (id, data)    => req('PUT',    `/customers/${id}`, data),
    delete: (id)          => req('DELETE', `/customers/${id}`),
    // Allocation sociétés du groupe
    getCompanies:     (id)              => req('GET',    `/customers/${id}/companies`),
    assignCompany:    (id, data)        => req('POST',   `/customers/${id}/companies`, data),
    unassignCompany:  (id, companyId)   => req('DELETE', `/customers/${id}/companies/${companyId}`),
    // Allocation commercial (interne ou externe)
    getSalesReps:     (id)              => req('GET',    `/customers/${id}/sales-reps`),
    assignSalesRep:   (id, data)        => req('POST',   `/customers/${id}/sales-reps`, data),
    unassignSalesRep: (id, allocId)     => req('DELETE', `/customers/${id}/sales-reps/${allocId}`),
    availableReps:    (companyId)       => req('GET',    `/customers/meta/available-reps${qs({ company_id: companyId })}`),
    // Activation / désactivation (soft-delete avec motif)
    setStatus:        (id, active, reason) => req('PATCH', `/customers/${id}/status`, { active, reason }),
    sirenLookup:      (number) => req('GET', `/customers/siren-lookup/${number}`),
    sirenSearch:      (q, limit) => req('GET', `/customers/siren-search${qs({ q, limit })}`),
    deletePermanent:  (id) => req('DELETE', `/customers/${id}/permanent`),
  },

  // ── Groupes de sociétés ─────────────────────────────────────
  companyGroups: {
    list:          ()                    => req('GET',    '/company-groups'),
    create:        (data)                => req('POST',   '/company-groups', data),
    update:        (id, data)            => req('PATCH',  `/company-groups/${id}`, data),
    delete:        (id)                  => req('DELETE', `/company-groups/${id}`),
    attachCompany: (groupId, companyId)  => req('PATCH',  `/company-groups/${groupId}/companies/${companyId}`),
    detachCompany: (groupId, companyId)  => req('DELETE', `/company-groups/${groupId}/companies/${companyId}`),
  },

  // ── Fournisseurs ──────────────────────────────────────────────
  suppliers: {
    list:   (cid, search) => req('GET',    `/suppliers${qs({ company_id: cid, search })}`),
    get:    (id)          => req('GET',    `/suppliers/${id}`),
    create: (data)        => req('POST',   '/suppliers', data),
    update: (id, data)    => req('PUT',    `/suppliers/${id}`, data),
    delete: (id)          => req('DELETE', `/suppliers/${id}`),
  },

  // ── Bons de livraison & Multilingue ──────────────────────────
  shippingNotes: {
    list:        (cid, p = {})  => req('GET',    `/shipping-notes${qs({ company_id: cid, ...p })}`),
    get:         (id)           => req('GET',    `/shipping-notes/${id}`),
    create:      (data)         => req('POST',   '/shipping-notes', data),
    update:      (id, data)     => req('PUT',    `/shipping-notes/${id}`, data),
    delete:      (id)           => req('DELETE', `/shipping-notes/${id}`),
    fromInvoice: (invId, data)  => req('POST',   `/shipping-notes/from-invoice/${invId}`, data),
    i18n:        ()             => req('GET',    '/shipping-notes/i18n'),
  },

  // ── CRM ───────────────────────────────────────────────────────
  crm: {
    stats:            (cid)              => req('GET',    `/crm/stats${qs({ company_id: cid })}`),
    pipeline:         (cid)              => req('GET',    `/crm/pipeline${qs({ company_id: cid })}`),
    contacts:         (cid, p = {})      => req('GET',    `/crm/contacts${qs({ company_id: cid, ...p })}`),
    contact:          (id)               => req('GET',    `/crm/contacts/${id}`),
    createContact:    (data)             => req('POST',   '/crm/contacts', data),
    updateContact:    (id, data)         => req('PUT',    `/crm/contacts/${id}`, data),
    deleteContact:    (id)               => req('DELETE', `/crm/contacts/${id}`),
    importContacts:   (data)             => req('POST',   '/crm/contacts/import', data),
    opportunities:    (cid, p = {})      => req('GET',    `/crm/opportunities${qs({ company_id: cid, ...p })}`),
    createOpp:        (data)             => req('POST',   '/crm/opportunities', data),
    updateOpp:        (id, data)         => req('PUT',    `/crm/opportunities/${id}`, data),
    activities:       (cid, p = {})      => req('GET',    `/crm/activities${qs({ company_id: cid, ...p })}`),
    createActivity:   (data)             => req('POST',   '/crm/activities', data),
    todayActivities:  (cid)              => req('GET',    `/crm/activities/today${qs({ company_id: cid })}`),
    reports:          (cid)              => req('GET',    `/crm/reports${qs({ company_id: cid })}`),
    // Commercial management
    commercials:      ()                 => req('GET',    '/crm/commercials'),
    assignContact:    (id, data)         => req('PATCH',  `/crm/contacts/${id}/assign`, data),
    contactHistory:   (id)               => req('GET',    `/crm/contacts/${id}/history`),
    portfolio:        (cid, commId)      => req('GET',    `/crm/portfolio${qs({ company_id: cid, commercial_id: commId })}`),
    teamStats:        (cid)              => req('GET',    `/crm/team-stats${qs({ company_id: cid })}`),
    unassigned:       (cid)              => req('GET',    `/crm/unassigned${qs({ company_id: cid })}`),
    inactivityAlerts: (cid, days)        => req('GET',    `/crm/inactivity-alerts${qs({ company_id: cid, days })}`),
    notifications:    ()                 => req('GET',    '/crm/notifications'),
    markNotifRead:    (id)               => req('PATCH',  `/crm/notifications/${id}/read`),
    markAllNotifsRead:()                 => req('PATCH',  '/crm/notifications/read-all'),
    commerciaux:      (cid)              => req('GET',    `/crm/commerciaux${qs({ company_id: cid })}`),
    bulkAssign:       (data)             => req('POST',   '/crm/contacts/bulk-assign', data),
    assignClient:     (id, data)         => req('PUT',    `/crm/clients/${id}/assign-commercial`, data),
  },

  // ── Prospection IA ────────────────────────────────────────────
  prospection: {
    campaigns:        (cid)           => req('GET',    `/prospection/campaigns${qs({ company_id: cid })}`),
    campaign:         (id)            => req('GET',    `/prospection/campaigns/${id}`),
    createCampaign:   (data)          => req('POST',   '/prospection/campaigns', data),
    updateCampaign:   (id, data)      => req('PUT',    `/prospection/campaigns/${id}`, data),
    generate:         (id, p = {})    => req('POST',   `/prospection/campaigns/${id}/generate`, p),
    leads:            (cid)           => req('GET',    `/prospection/leads${qs({ campaign_id: cid })}`),
    leadStatus:       (id, status)    => req('PATCH',  `/prospection/leads/${id}/status`, { status }),
    leadToCrm:        (id)            => req('POST',   `/prospection/leads/${id}/to-crm`, {}),
    generateEmail:    (id, p = {})    => req('POST',   `/prospection/leads/${id}/email`, p),
  },

  // ── Abonnements récurrents ────────────────────────────────────
  subscriptions: {
    list:           (p = {})     => req('GET',    `/subscriptions${qs(p)}`),
    get:            (id)         => req('GET',    `/subscriptions/${id}`),
    stats:          ()           => req('GET',    '/subscriptions/stats'),
    dueSoon:        (days)       => req('GET',    `/subscriptions/due-soon${qs({ days })}`),
    calendar:       (m, y)       => req('GET',    `/subscriptions/calendar${qs({ month: m, year: y })}`),
    billingHistory: ()           => req('GET',    '/subscriptions/billing/history'),
    invoices:       (id)         => req('GET',    `/subscriptions/${id}/invoices`),
    create:         (data)       => req('POST',   '/subscriptions', data),
    update:         (id, data)   => req('PUT',    `/subscriptions/${id}`, data),
    delete:         (id)         => req('DELETE', `/subscriptions/${id}`),
    generateInvoice:(id)         => req('POST',   `/subscriptions/${id}/invoice`),
    suspend:        (id)         => req('POST',   `/subscriptions/${id}/suspend`),
    cancel:         (id)         => req('POST',   `/subscriptions/${id}/cancel`),
  },

  // ── Onboarding / Configuration initiale ──────────────────────
  onboarding: {
    status:              ()               => req('GET',  '/onboarding/status'),
    complete:            (data)           => req('POST', '/onboarding/complete', data),
    // Questionnaire activité/priorités (avant le wizard)
    saveActivityProfile: (data)           => req('POST', '/onboarding/activity-profile', data),
    // Wizard v2 — step-by-step
    saveCompany:         (data)           => req('POST', '/onboarding/company', data),
    saveBilling:         (data)           => req('POST', '/onboarding/billing', data),
    saveFirstCustomer:   (data)           => req('POST', '/onboarding/first-customer', data),
    saveBank:            (data)           => req('POST', '/onboarding/bank', data),
    markComplete:        ()               => req('PUT',  '/onboarding/complete'),
    // Données d'exemple de l'inscription (société active uniquement)
    demoData:            ()               => req('GET',    '/onboarding/demo-data'),
    purgeDemoData:       ()               => req('DELETE', '/onboarding/demo-data'),
    // Paramètres comptables
    accountingSettings:  (cid)            => req('GET',  `/onboarding/accounting-settings/${cid}`),
    saveAccountingSettings:(cid, data)    => req('PUT',  `/onboarding/accounting-settings/${cid}`, data),
    openingBalances:     (cid)            => req('GET',  `/onboarding/opening-balances/${cid}`),
    saveOpeningBalances: (cid, data)      => req('POST', `/onboarding/opening-balances/${cid}`, data),
    // Checklist personnalisée (dashboard)
    checklist:           (cid)            => req('GET',  `/onboarding/checklist/${cid}`),
  },

  // ── Notifications ─────────────────────────────────────────────
  notifications: {
    list:          (params = {})  => req('GET',    '/notifications' + (params.tab ? `?tab=${params.tab}&page=${params.page||1}` : '')),
    unreadCount:   ()             => req('GET',    '/notifications/unread-count'),
    markRead:      (id)           => req('PUT',    `/notifications/${id}/read`),
    markAllRead:   ()             => req('PUT',    '/notifications/read-all'),
    archive:       (id)           => req('DELETE', `/notifications/${id}`),
    getSettings:   ()             => req('GET',    '/notifications/settings'),
    saveSettings:  (data)         => req('PUT',    '/notifications/settings', data),
    generate:      ()             => req('POST',   '/notifications/generate'),
  },

  // ── FEC avancé ────────────────────────────────────────────────
  fecAdvanced: {
    analyze:          (cid, data)        => req('POST', `/fec/analyze/${cid}`, data),
    smartImport:      (cid, data)        => req('POST', `/fec/import/${cid}`, data),
    importPreview:    (cid, data)        => req('POST', `/fec/import-preview/${cid}`, data), // à supprimer / à insérer / refus
    status:           (cid)              => req('GET',  `/fec/status/${cid}`),
    imports:          (cid)              => req('GET',  `/fec/imports/${cid}`),
    report:           (importId)         => req('GET',  `/fec/imports/report/${importId}`),
    validate:         (cid, year)        => req('GET',  `/fec/validate/${cid}?year=${year || ''}`),
    exportUrl:        (cid, year)        => `${BASE}/fec/export/${cid}?year=${year || new Date().getFullYear()}`,
    checkContinuity:  (data)             => req('POST', '/fec/check-continuity', data),
  },

  // ── Fusion de sociétés ────────────────────────────────────────
  mergers: {
    list:     ()             => req('GET',  '/mergers'),
    get:      (id)           => req('GET',  `/mergers/${id}`),
    preview:  (src, tgt)     => req('GET',  `/mergers/preview?source_id=${src}&target_id=${tgt}`),
    create:   (data)         => req('POST', '/mergers', data),
    update:   (id, data)     => req('PUT',  `/mergers/${id}`, data),
    execute:  (id)           => req('POST', `/mergers/${id}/execute`, {}),
  },

  // ── Import relevés bancaires ──────────────────────────────────
  bankImport: {
    analyze:       (cid, data)     => req('POST', `/bank-import/analyze/${cid}`, data),
    import:        (cid, data)     => req('POST', `/bank-import/import/${cid}`, data),
    staging:       (cid, params)   => req('GET',  `/bank-import/staging/${cid}${qs(params||{})}`),
    updateStaging: (id, data)      => req('PUT',  `/bank-import/staging/${id}`, data),
    validate:      (importId, ids) => req('POST', `/bank-import/validate/${importId}`, ids ? { ids } : {}),
    // Recalcule les rapprochements des lignes en attente (factures validées après l'import)
    rematch:       (importId)      => req('POST', `/bank-import/rematch/${importId}`, {}),
    imports:       (cid)           => req('GET',  `/bank-import/imports/${cid}`),
    deleteImport:  (cid, id)       => req('DELETE', `/bank-import/imports/${cid}/${id}`), // relevé importé par erreur (administrateur)
    // Recalcul des suggestions d'un relevé : aperçu, application, annulation du dernier recalcul
    resuggestLast:    (importId)   => req('GET',  `/bank-import/resuggest/${importId}/last`),
    resuggestPreview: (importId)   => req('POST', `/bank-import/resuggest/${importId}/preview`, {}),
    resuggestApply:   (importId)   => req('POST', `/bank-import/resuggest/${importId}/apply`, {}),
    resuggestUndo:    (importId)   => req('POST', `/bank-import/resuggest/${importId}/undo`, {}),
    // Annuler la validation d'une ligne : aperçu de l'extourne, puis exécution
    unvalidatePreview: (id)        => req('GET',  `/bank-import/staging/${id}/unvalidate`),
    unvalidate:        (id)        => req('POST', `/bank-import/staging/${id}/unvalidate`, {}),
    // Ventilation d'une ligne en attente sur plusieurs factures (lot 2)
    allocationCandidates: (id)       => req('GET',  `/bank-import/staging/${id}/allocation/candidates`),
    allocationPreview:    (id, data) => req('POST', `/bank-import/staging/${id}/allocation/preview`, data),
    allocationConfirm:    (id, data) => req('POST', `/bank-import/staging/${id}/allocation/confirm`, data),
    // Rattachement a posteriori d'une ligne validée / reclassement (lot 2)
    attachCandidates: (id, account) => req('GET', `/bank-import/staging/${id}/attach/candidates${account ? `?account=${encodeURIComponent(account)}` : ''}`),
    attachPreview:    (id, data) => req('POST', `/bank-import/staging/${id}/attach/preview`, data),
    attachConfirm:    (id, data) => req('POST', `/bank-import/staging/${id}/attach/confirm`, data),
    attachCancel:     (id)       => req('POST', `/bank-import/staging/${id}/attach/cancel`, {}),
    // Propositions automatiques de factures pour les encaissements (28/10/2026)
    suggestions:        (importId) => req('GET',  `/bank-import/suggestions${qs(importId ? { import_id: importId } : {})}`),
    suggestionsConfirm: (items)    => req('POST', '/bank-import/suggestions/confirm', { items }),
    // Remises de virements fournisseurs (B1b) : fichier CSV, aperçu puis confirmation ligne par ligne
    remisesPreview:  (data)       => req('POST', '/bank-import/remises/preview', data),
    remisesConfirm:  (data)       => req('POST', '/bank-import/remises/confirm', data),
    remiseOf:        (id)         => req('GET', `/bank-import/staging/${id}/remise`),
    remisesTemplate: ()           => reqBlobFile('/bank-import/remises/template.csv'),
  },

  // ── Rattrapage comptable ──────────────────────────────────────
  catchup: {
    dashboard:    ()                   => req('GET',  '/catchup/dashboard'),
    session:      (cid, year)          => req('GET',  `/catchup/session/${cid}/${year}`),
    createSession:(cid, data)          => req('POST', `/catchup/session/${cid}`, data),
    validateMonth:(sid, month, data)   => req('PUT',  `/catchup/month/${sid}/${month}`, data),
    monthTva:     (cid, year, month)   => req('GET',  `/catchup/month-tva/${cid}/${year}/${month}`),
  },

  // ── Documents PDF ────────────────────────────────────────────
  documents: {
    invoicePdf: (id, lang = 'fr', type = 'facture') =>
      reqBlob(`/documents/invoice/${id}/pdf?lang=${lang}&type=${type}`),
    blPdf: (id, lang = 'fr') =>
      reqBlob(`/documents/bl/${id}/pdf?lang=${lang}`),
  },

  // ── Commandes clients multi-canaux ───────────────────────────────────
  customerOrders: {
    templateDownload: ()       => reqBlobFile('/customer-orders/template'),
    stats:          (cid)         => req('GET',    `/customer-orders/stats${qs({ company_id: cid })}`),
    list:           (cid, p={})   => req('GET',    `/customer-orders${qs({ company_id: cid, ...p })}`),
    get:            (id)          => req('GET',    `/customer-orders/${id}`),
    create:         (data)        => req('POST',   '/customer-orders', data),
    update:         (id, data)    => req('PUT',    `/customer-orders/${id}`, data),
    status:         (id, status)  => req('PATCH',  `/customer-orders/${id}/status`, { status }),
    delete:         (id)          => req('DELETE', `/customer-orders/${id}`),
    createPrep:     (id, data)    => req('POST',   `/customer-orders/${id}/create-preparation`, data),
    importExcel:    (data)        => req('POST',   '/customer-orders/import-excel', data),
    templateUrl:    ()            => `${BASE}/customer-orders/template`,
    ocrPdf:         (data)        => req('POST',   '/customer-orders/ocr-pdf', data),
    exportUrl:      (cid)         => `${BASE}/customer-orders/export${qs({ company_id: cid })}`,
    shippingLabel:  (id)          => `${BASE}/customer-orders/${id}/shipping-label`,
    apiKeys:        (cid)         => req('GET',    `/customer-orders/api-keys${qs({ company_id: cid })}`),
    createApiKey:   (data)        => req('POST',   '/customer-orders/api-keys', data),
    deleteApiKey:   (id)          => req('DELETE', `/customer-orders/api-keys/${id}`),
    toggleApiKey:   (id)          => req('PATCH',  `/customer-orders/api-keys/${id}/toggle`),
  },

  // ── Comptabilité avancée (CCA, Prêts, Escomptes, Escompte bancaire) ──
  accounting: {
    listCCA:          (cid)          => req('GET',    `/accounting/cca${qs({ company_id: cid })}`),
    createCCA:        (data)         => req('POST',   '/accounting/cca', data),
    updateCCA:        (id, data)     => req('PUT',    `/accounting/cca/${id}`, data),
    deleteCCA:        (id)           => req('DELETE', `/accounting/cca/${id}`),
    ccaTransactions:  (id)           => req('GET',    `/accounting/cca/${id}/transactions`),
    addCCATx:         (id, data)     => req('POST',   `/accounting/cca/${id}/transactions`, data),
    deleteCCATx:      (id, txId)     => req('DELETE', `/accounting/cca/${id}/transactions/${txId}`),
    computeInterests: (id, data)     => req('POST',   `/accounting/cca/${id}/compute-interests`, data),
    ccaPdfUrl:        (id)           => `${BASE}/accounting/cca/${id}/pdf`,
    ccaPdfDownload:   (id)           => reqBlobFile(`/accounting/cca/${id}/pdf`),
    loans:            (cid)          => req('GET',    `/accounting/loans${qs({ company_id: cid })}`),
    createLoan:       (data)         => req('POST',   '/accounting/loans', data),
    updateLoan:       (id, data)     => req('PUT',    `/accounting/loans/${id}`, data),
    deleteLoan:       (id)           => req('DELETE', `/accounting/loans/${id}`),
    discounts:        (cid, type)    => req('GET',    `/accounting/discounts${qs({ company_id: cid, type })}`),
    createDiscount:   (data)         => req('POST',   '/accounting/discounts', data),
    updateDiscount:   (id, data)     => req('PUT',    `/accounting/discounts/${id}`, data),
    deleteDiscount:   (id)           => req('DELETE', `/accounting/discounts/${id}`),
    bankDiscounting:  (cid, status)  => req('GET',    `/accounting/bank-discounting${qs({ company_id: cid, status })}`),
    createBankDisc:   (data)         => req('POST',   '/accounting/bank-discounting', data),
    updateBankDisc:   (id, data)     => req('PUT',    `/accounting/bank-discounting/${id}`, data),
    bankDiscStatus:   (id, status)   => req('PATCH',  `/accounting/bank-discounting/${id}/status`, { status }),
    deleteBankDisc:   (id)           => req('DELETE', `/accounting/bank-discounting/${id}`),
    health:           ()             => req('GET',    '/accounting/health'),
    healthHistory:    (limit)        => req('GET',    `/accounting/health/history${qs({ limit })}`),
    healthRunNow:     ()             => req('POST',   '/accounting/health/run-now', {}),
  },

  // ── Abonnements (fournisseurs + clients + Brevo) ─────────────
  abonnements: {
    supplierDashboard:   (cid)          => req('GET',    `/abonnements/supplier-subs/dashboard${qs({ company_id: cid })}`),
    supplierSubs:        (cid, p={})    => req('GET',    `/abonnements/supplier-subs${qs({ company_id: cid, ...p })}`),
    createSupplierSub:   (data)         => req('POST',   '/abonnements/supplier-subs', data),
    updateSupplierSub:   (id, data)     => req('PUT',    `/abonnements/supplier-subs/${id}`, data),
    deleteSupplierSub:   (id)           => req('DELETE', `/abonnements/supplier-subs/${id}`),
    subInvoices:         (id)           => req('GET',    `/abonnements/supplier-subs/${id}/invoices`),
    supplierInvoices:    (cid, p={})    => req('GET',    `/abonnements/supplier-invoices${qs({ company_id: cid, ...p })}`),
    ocrInvoice:          (data)         => req('POST',   '/abonnements/supplier-invoices/ocr', data),
    createSupplierInv:   (data)         => req('POST',   '/abonnements/supplier-invoices', data),
    processInvoice:      (id)           => req('PATCH',  `/abonnements/supplier-invoices/${id}/process`, {}),
    getImapConfig:       (cid)          => req('GET',    `/abonnements/imap-config${qs({ company_id: cid })}`),
    saveImapConfig:      (data)         => req('POST',   '/abonnements/imap-config', data),
    emailImports:        (cid)          => req('GET',    `/abonnements/email-imports${qs({ company_id: cid })}`),
    processEmailImport:  (id, status)   => req('PATCH',  `/abonnements/email-imports/${id}/status`, { status }),
    clientPlans:         (cid)          => req('GET',    `/abonnements/client-plans${qs({ company_id: cid })}`),
    createClientPlan:    (data)         => req('POST',   '/abonnements/client-plans', data),
    updateClientPlan:    (id, data)     => req('PUT',    `/abonnements/client-plans/${id}`, data),
    deleteClientPlan:    (id)           => req('DELETE', `/abonnements/client-plans/${id}`),
    reorderPlans:        (data)         => req('PATCH',  '/abonnements/client-plans/reorder', data),
    clientSubs:          (cid, p={})    => req('GET',    `/abonnements/client-subs${qs({ company_id: cid, ...p })}`),
    createClientSub:     (data)         => req('POST',   '/abonnements/client-subs', data),
    updateClientSub:     (id, data)     => req('PUT',    `/abonnements/client-subs/${id}`, data),
    clientSubStatus:     (id, s, r)     => req('PATCH',  `/abonnements/client-subs/${id}/status`, { status: s, cancel_reason: r }),
    deleteClientSub:     (id)           => req('DELETE', `/abonnements/client-subs/${id}`),
    subPayments:         (id)           => req('GET',    `/abonnements/client-subs/${id}/payments`),
    sendPaymentLink:     (id)           => req('POST',   `/abonnements/client-subs/${id}/send-payment-link`, {}),
    addPayment:          (data)         => req('POST',   '/abonnements/payments', data),
    metrics:             (cid)          => req('GET',    `/abonnements/metrics${qs({ company_id: cid })}`),
    brevoConfig:         (cid)          => req('GET',    `/abonnements/brevo-config${qs({ company_id: cid })}`),
    saveBrevoConfig:     (data)         => req('POST',   '/abonnements/brevo-config', data),
    testBrevo:           (key)          => req('POST',   '/abonnements/brevo/test', { api_key: key }),
    brevoTemplates:      (cid)          => req('GET',    `/abonnements/brevo/templates${qs({ company_id: cid })}`),
    brevoLists:          (cid)          => req('GET',    `/abonnements/brevo/lists${qs({ company_id: cid })}`),
    brevoSync:           (data)         => req('POST',   '/abonnements/brevo/sync', data),
    brevoCampaigns:      (cid, status)  => req('GET',    `/abonnements/brevo/campaigns${qs({ company_id: cid, status })}`),
    createCampaign:      (data)         => req('POST',   '/abonnements/brevo/campaigns', data),
    sendCampaign:        (id, cid)      => req('POST',   `/abonnements/brevo/campaigns/${id}/send`, { company_id: cid }),
    campaignStats:       (id, cid)      => req('GET',    `/abonnements/brevo/campaigns/${id}/stats${qs({ company_id: cid })}`),
    crmSegment:          (data)         => req('POST',   '/abonnements/brevo/crm-segment', data),
  },

  // ── Factures fournisseurs (achats validés) ───────────────────
  purchases: {
    list:    (p = {})  => req('GET',  `/purchases${qs(p)}`),
    months:  ()        => req('GET',  '/purchases/months'), // onglets « À payer » et par mois
    settledMark: (ids, marked) => req('POST', '/purchases/settled-mark', { ids, marked }), // « Déjà réglée — à rapprocher » (J1)
    toValidate: ()       => req('GET',  '/purchases/to-validate'), // achats et sorties du relevé à valider (J1)
    get:     (id)       => req('GET',  `/purchases/${id}`),
    create:  (data)      => req('POST', '/purchases', data),
    update:  (id, data)  => req('PUT',  `/purchases/${id}`, data),
    validate:(id)        => req('PATCH', `/purchases/${id}/validate`, {}),
    cancel:  (id, reason)=> req('POST', `/purchases/${id}/cancel`, { reason }),
    cancelPreview: (id)  => req('GET',  `/purchases/${id}/cancel`), // contre-passation prévue ou motif de refus
    delete:  (id)        => req('DELETE', `/purchases/${id}`),
    entries: (id)        => req('GET',  `/purchases/${id}/entries`),
    natureMissing: ()    => req('GET',  '/purchases/nature-missing'),
    setNature: (id, data) => req('POST', `/purchases/${id}/nature`, data),
    // Achat validé : changer la nature (aperçu de l'OD, puis exécution)
    natureChangePreview: (id, nature, onDebits) => req('GET', `/purchases/${id}/nature-change${qs({ operation_nature: nature, supplier_vat_on_debits: onDebits ? 'true' : 'false' })}`),
    natureChange: (id, data) => req('POST', `/purchases/${id}/nature-change`, data),
  },

  // ── Démarrage (nouvel abonné) et synthèse TVA ──────────────
  demarrage: {
    get:       (cid) => req('GET',  `/demarrage/${cid}`),
    noOpening: (cid) => req('POST', `/demarrage/${cid}/no-opening`, {}),
  },
  vatSummary: {
    get:       (cid, month) => req('GET', `/vat-summary/${cid}${qs({ month })}`),
    exportXlsx:(cid, month) => reqBlobFile(`/vat-summary/${cid}/export.xlsx${qs({ month })}`),
    exportPdf: (cid, month) => reqBlobFile(`/vat-summary/${cid}/export.pdf${qs({ month })}`),
    // Concordance avec le journal des ventes Dolibarr : { month, filename, content_base64 }
    concordance:     (cid, data) => req('POST', `/vat-summary/${cid}/concordance`, data),
    concordanceXlsx: (cid, data) => reqBlobFile(`/vat-summary/${cid}/concordance.xlsx`, data),
    // Déclaration déposée : { month, filed_on, tva_collectee, tva_deductible, tva_nette }
    declarations:      (cid)             => req('GET',  `/vat-summary/${cid}/declarations`),
    declarationPrerequisites: (cid, month) => req('GET', `/vat-summary/${cid}/declarations/prerequisites?month=${encodeURIComponent(month)}`),
    recordDeclaration: (cid, data)       => req('POST', `/vat-summary/${cid}/declarations`, data),
    cancelDeclaration: (cid, id, reason) => req('POST', `/vat-summary/${cid}/declarations/${id}/cancel`, { reason }),
  },
  // Plan comptable propre à la société : { filename, content_base64, dry_run }
  chartAccounts: {
    list:   (cid)       => req("GET",  `/chart-accounts/${cid}`),
    choices: (cid)      => req("GET",  `/chart-accounts/${cid}/choices`), // plan + tiers nommés + codes sans fiche (A1a)
    import: (cid, data) => req("POST", `/chart-accounts/${cid}/import`, data),
    create: (cid, data) => req("POST", `/chart-accounts/${cid}/accounts`, data),
  },
  // Codes de tiers de l'expert-comptable — mode reprise d'activité (28/10/2026)
  tiersCodes: {
    get:     ()     => req("GET", "/tiers-codes"),
    setMode: (mode) => req("PUT", "/tiers-codes/mode", { mode }),
    setCode: (customerId, code) => req("PUT", `/tiers-codes/customers/${customerId}`, { code }),
    setSupplierCode: (supplierAccountId, code) => req("PUT", `/tiers-codes/suppliers/${supplierAccountId}`, { code }),
    import:  (data) => req("POST", "/tiers-codes/import", data),
    renamePlan: () => req("GET", "/tiers-codes/rename"),
    rename:  (fingerprint) => req("POST", "/tiers-codes/rename", { fingerprint }),
    duplicates:   () => req("GET", "/tiers-codes/duplicates"),
    pdfProposals: () => req("POST", "/tiers-codes/pdf-proposals", {}),
    pdfApply:     (customer_ids) => req("POST", "/tiers-codes/pdf-proposals/apply", { customer_ids }),
    orphans:      () => req("GET", "/tiers-codes/orphans"), // codes 401/411 sans fiche (T)
    orphansApply: (selections) => req("POST", "/tiers-codes/orphans/apply", { selections }),
    mergePreview: (target_id, source_ids) => req("POST", "/tiers-codes/merge/preview", { target_id, source_ids }),
    merge:        (target_id, source_ids, fingerprint) => req("POST", "/tiers-codes/merge", { target_id, source_ids, fingerprint }),
  },

  // ── Budget & Prévisionnel ─────────────────────────────────
  ocrInvoices: {
    // batchId : identifiant commun aux fichiers d'une même sélection (annulation du lot)
    upload:   (cid, file, batchId) => { const fd = new FormData(); if (batchId) fd.append('batch_id', batchId); fd.append('file', file); return reqUpload(`/supplier-invoices-staging/upload/${cid}`, fd); },
    list:     (cid, p = {})    => req('GET',  `/supplier-invoices-staging/${cid}${qs(p)}`),
    // Factures de l'exercice antérieur (reprises par FEC) : rubrique séparée, jamais à valider
    priorExercise: (cid)       => req('GET',  `/supplier-invoices-staging/prior-exercise/${cid}`),
    update:   (id, data)       => req('PUT',  `/supplier-invoices-staging/${id}`, data),
    validate: (id, data = {})  => req('POST', `/supplier-invoices-staging/${id}/validate`, data),
    validateBatch: (cid, ids) => req('POST', `/supplier-invoices-staging/validate-batch/${cid}`, ids ? { ids } : {}),
    reject:   (id, reason, deletePdf = false) => req('POST', `/supplier-invoices-staging/${id}/reject`, { reason, delete_pdf: deletePdf }),
    rejectBulk:  (ids, reason, deletePdf = false)     => req('POST', `/supplier-invoices-staging/reject-bulk`, { ids, reason, delete_pdf: deletePdf }),
    rejectBatch: (batchId, reason, deletePdf = false) => req('POST', `/supplier-invoices-staging/batches/${encodeURIComponent(batchId)}/reject`, { reason, delete_pdf: deletePdf }),
    // PDF importé — via reqBlob (avec jeton), jamais en lien direct
    file:     (id)             => reqBlob(`/supplier-invoices-staging/${id}/file`),
  },

  salesOcrInvoices: {
    upload:   (cid, file)      => { const fd = new FormData(); fd.append('file', file); return reqUpload(`/sales-invoices-staging/upload/${cid}`, fd); },
    list:     (cid, p = {})    => req('GET',  `/sales-invoices-staging/${cid}${qs(p)}`),
    update:   (id, data)       => req('PUT',  `/sales-invoices-staging/${id}`, data),
    validate: (id, data = {})  => req('POST', `/sales-invoices-staging/${id}/validate`, data),
    reject:   (id, reason)     => req('POST', `/sales-invoices-staging/${id}/reject`, { reason }),
    file:     (id)             => reqBlob(`/sales-invoices-staging/${id}/file`),
    // Import Dolibarr : un PDF par appel ; export CSV/Excel en base64 ; PDF d'origine d'une facture
    dolibarrUpload: (cid, file, batchId, opts = {}) => { const fd = new FormData(); if (batchId) fd.append('batch_id', batchId); if (opts.withExport) fd.append('with_export', '1'); fd.append('file', file, file.name); return reqUpload(`/sales-invoices-staging/dolibarr/upload/${cid}`, fd); },
    dolibarrExport: (cid, data) => req('POST', `/sales-invoices-staging/dolibarr/export/${cid}`, data),
    validateBatch: (cid, ids, operationNature) => req('POST', `/sales-invoices-staging/validate-batch/${cid}`, { ...(ids ? { ids } : {}), ...(operationNature ? { operation_nature: operationNature } : {}) }),
    dolibarrOpeningPdf: (cid, file) => { const fd = new FormData(); fd.append('file', file, file.name); return reqUpload(`/sales-invoices-staging/dolibarr/opening-pdf/${cid}`, fd); },
    dolibarrOpening: (cid, data) => req('POST', `/sales-invoices-staging/dolibarr/opening/${cid}`, data),
    // Journal des ventes Dolibarr → répartition du HT par compte 70 : { filename, content_base64 }
    importJournal:  (cid, data) => req('POST', `/sales-invoices-staging/dolibarr/journal/${cid}`, data),
    fileByInvoice:  (invoiceId) => reqBlob(`/sales-invoices-staging/by-invoice/${invoiceId}/file`),
  },
  // Corrections des ventes validées : aperçu des écritures, puis confirmation par le client
  salesCorrections: {
    summary:           (cid)      => req('GET',  `/sales-corrections/${cid}/summary`),
    exigibilityList:   (cid)      => req('GET',  `/sales-corrections/${cid}/exigibility/candidates`),
    exigibilityPreview: (cid, ids) => req('POST', `/sales-corrections/${cid}/exigibility/preview`, { invoice_ids: ids }),
    exigibilityApply:  (cid, ids) => req('POST', `/sales-corrections/${cid}/exigibility/apply`, { invoice_ids: ids }),
    reclassAnalyse:    (cid, file) => req('POST', `/sales-corrections/${cid}/reclass/analyse`, file || {}),
    reclassPreview:    (cid, file, ids) => req('POST', `/sales-corrections/${cid}/reclass/preview`, { ...file, invoice_ids: ids }),
    reclassApply:      (cid, file, ids) => req('POST', `/sales-corrections/${cid}/reclass/apply`, { ...file, invoice_ids: ids }),
  },
  // Acomptes d'ouverture (modèle Excel, aperçu, création, registre)
  // « Mon mois comptable » : lecture seule
  monthOverview: { get: (month) => req("GET", `/month-overview?month=${encodeURIComponent(month)}`) },
  // « Signaler un problème » (04/11/2026) : message, page, capture facultative
  problemReports: {
    create:     (data) => req("POST", "/problem-reports", data),
    list:       ()     => req("GET", "/problem-reports"),          // équipe ExecPro360
    screenshot: (id)   => reqBlob(`/problem-reports/${id}/screenshot`),
  },
  openingDeposits: {
    template: ()          => reqBlobFile('/opening-deposits/template.xlsx'),
    list:     (cid)       => req('GET',  `/opening-deposits/${cid}`),
    preview:  (cid, data) => req('POST', `/opening-deposits/${cid}/preview`, data),
    create:   (cid, data) => req('POST', `/opening-deposits/${cid}/create`, data),
    // B7 : PDF d'une facture d'acompte Dolibarr (AC…), lu sans IA → { row }
    readPdf:  (cid, file) => { const fd = new FormData(); fd.append('file', file); return reqUpload(`/opening-deposits/${cid}/pdf`, fd); },
  },

  // ── Budget & Prévisionnel ─────────────────────────────────────
  budget: {
    scenarios:         (cid, year)      => req('GET',    `/budget/scenarios${qs({ company_id: cid, year })}`),
    createScenario:    (data)           => req('POST',   '/budget/scenarios', data),
    updateScenario:    (id, data)       => req('PUT',    `/budget/scenarios/${id}`, data),
    deleteScenario:    (id)             => req('DELETE', `/budget/scenarios/${id}`),
    validateScenario:  (id)             => req('POST',   `/budget/scenarios/${id}/validate`, {}),
    archiveScenario:   (id)             => req('POST',   `/budget/scenarios/${id}/archive`, {}),
    duplicateScenario: (id, data)       => req('POST',   `/budget/scenarios/${id}/duplicate`, data),
    lines:             (id)             => req('GET',    `/budget/scenarios/${id}/lines`),
    saveLine:          (id, data)       => req('POST',   `/budget/scenarios/${id}/lines`, data),
    batchSave:         (id, data)       => req('PUT',    `/budget/scenarios/${id}/lines/batch`, data),
    deleteLine:        (id, lineId)     => req('DELETE', `/budget/scenarios/${id}/lines/${lineId}`),
    addCustomLine:     (id, data)       => req('POST',   `/budget/scenarios/${id}/lines/custom`, data),
    copyPriorYear:     (id, data)       => req('POST',   `/budget/scenarios/${id}/copy-prior-year`, data),
    syncActuals:       (id, data)       => req('POST',   `/budget/scenarios/${id}/sync-actuals`, data),
    dashboard:         (id)             => req('GET',    `/budget/scenarios/${id}/dashboard`),
    comparison:        (id, p={})       => req('GET',    `/budget/scenarios/${id}/comparison${qs(p)}`),
    compareScenarios:  (a, b)           => req('GET',    `/budget/compare${qs({ a, b })}`),
    cash:              (id)             => req('GET',    `/budget/scenarios/${id}/cash`),
    saveCash:          (id, data)       => req('POST',   `/budget/scenarios/${id}/cash`, data),
    deleteCashLine:    (id, lineId)     => req('DELETE', `/budget/scenarios/${id}/cash/${lineId}`),
    syncCash:          (id, data)       => req('POST',   `/budget/scenarios/${id}/sync-cash`, data),
    importExcel:       (id, data)       => req('POST',   `/budget/scenarios/${id}/import-excel`, data),
    aiAnalysis:        (id)             => req('POST',   `/budget/scenarios/${id}/ai-analysis`, {}),
    businessPlan:      (id, data)       => req('POST',   `/budget/scenarios/${id}/business-plan`, data),
    generateVariants:      (id, data)   => req('POST',   `/budget/scenarios/${id}/generate-variants`, data),
    breakeven:             (id)         => req('GET',    `/budget/scenarios/${id}/breakeven`),
    projects:              (cid)        => req('GET',    `/budget/projects${qs({ company_id: cid })}`),
    createProject:         (data)       => req('POST',   '/budget/projects', data),
    updateProject:         (id, data)   => req('PUT',    `/budget/projects/${id}`, data),
    deleteProject:         (id)         => req('DELETE', `/budget/projects/${id}`),
    compareMulti:          (ids)        => req('GET',    `/budget/compare-multi${qs({ ids: ids.join(',') })}`),
    consolidated:          (cids, year) => req('GET',    `/budget/consolidated${qs({ company_ids: cids.join(','), year })}`),
    exportBudgetUrl:       (id)         => `${BASE}/budget/scenarios/${id}/export/excel-budget`,
    exportComparisonUrl:   (id)         => `${BASE}/budget/scenarios/${id}/export/excel-comparison`,
    exportCashUrl:         (id)         => `${BASE}/budget/scenarios/${id}/export/excel-cash`,
    exportPdfUrl:          (id)         => `${BASE}/budget/scenarios/${id}/export/pdf`,
    exportScenariosUrl:    (ids)        => `${BASE}/budget/export/scenarios-comparison?ids=${ids.join(',')}`,
    templateUrl:           ()           => `${BASE}/budget/template`,
  },

  // ── Immobilisations ───────────────────────────────────────────
  immobilisations: {
    exportRegisterDownload: () => reqBlobFile('/immobilisations/export/register'),
    dashboard:        (cid)         => req('GET',    `/immobilisations/dashboard${qs({ company_id: cid })}`),
    list:             (cid, p={})   => req('GET',    `/immobilisations${qs({ company_id: cid, ...p })}`),
    get:              (id)          => req('GET',    `/immobilisations/${id}`),
    create:           (data)        => req('POST',   '/immobilisations', data),
    update:           (id, data)    => req('PUT',    `/immobilisations/${id}`, data),
    delete:           (id)          => req('DELETE', `/immobilisations/${id}`),
    previewPlan:      (data)        => req('POST',   '/immobilisations/preview-plan', data),
    postDepreciation: (id, data)    => req('POST',   `/immobilisations/${id}/post-depreciation`, data),
    postAllDepreciation:(data)      => req('POST',   '/immobilisations/post-all-depreciation', data),
    dispose:          (id, data)    => req('POST',   `/immobilisations/${id}/dispose`, data),
    uploadImage:      (id, data)    => req('POST',   `/immobilisations/${id}/image`, data),
    exportRegisterUrl:(cid)         => `${BASE}/immobilisations/export/register${qs({ company_id: cid })}`,
    exportPlanUrl:    (cid)         => `${BASE}/immobilisations/export/plan${qs({ company_id: cid })}`,
    exportPdfUrl:     (cid, year)   => `${BASE}/immobilisations/export/pdf/${year}${qs({ company_id: cid })}`,
  },
  multilingues: {
    // Langues
    languages:       ()              => req('GET',    '/documents-multilingues/languages'),
    createLanguage:  (data)          => req('POST',   '/documents-multilingues/languages', data),
    updateLanguage:  (code, data)    => req('PUT',    `/documents-multilingues/languages/${code}`, data),
    deleteLanguage:  (code)          => req('DELETE', `/documents-multilingues/languages/${code}`),
    // Traductions
    translations:    (p={})          => req('GET',    `/documents-multilingues/translations${qs(p)}`),
    saveBatch:       (data)          => req('POST',   '/documents-multilingues/translations/batch', data),
    autoTranslate:   (data)          => req('POST',   '/documents-multilingues/translations/auto-translate', data),
    deleteTranslation:(id)           => req('DELETE', `/documents-multilingues/translations/${id}`),
    fieldKeys:       ()              => req('GET',    '/documents-multilingues/field-keys'),
    // Devises
    currencies:      (p={})          => req('GET',    `/documents-multilingues/currencies${qs(p)}`),
    createCurrency:  (data)          => req('POST',   '/documents-multilingues/currencies', data),
    updateCurrency:  (code, data)    => req('PUT',    `/documents-multilingues/currencies/${code}`, data),
    deleteCurrency:  (code)          => req('DELETE', `/documents-multilingues/currencies/${code}`),
    refreshRates:    ()              => req('POST',   '/documents-multilingues/currencies/refresh-rates', {}),
    // Mentions légales
    legalMentions:   (p={})          => req('GET',    `/documents-multilingues/legal-mentions${qs(p)}`),
    createMention:   (data)          => req('POST',   '/documents-multilingues/legal-mentions', data),
    updateMention:   (id, data)      => req('PUT',    `/documents-multilingues/legal-mentions/${id}`, data),
    deleteMention:   (id)            => req('DELETE', `/documents-multilingues/legal-mentions/${id}`),
    // Documents export
    exportDocs:      (cid)           => req('GET',    `/documents-multilingues/export-docs${qs({ company_id: cid })}`),
    generateEur1:    (data)          => fetch(`${BASE}/documents-multilingues/export-docs/eur1`, { method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${getToken()}`}, body: JSON.stringify(data) }).then(r => r.blob()),
    generatePackingList:(data)       => fetch(`${BASE}/documents-multilingues/export-docs/packing-list`, { method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${getToken()}`}, body: JSON.stringify(data) }).then(r => r.blob()),
    generateCustomsInv:(data)        => fetch(`${BASE}/documents-multilingues/export-docs/customs-invoice`, { method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${getToken()}`}, body: JSON.stringify(data) }).then(r => r.blob()),
    generateDeb:     (data)          => fetch(`${BASE}/documents-multilingues/export-docs/deb-xml`, { method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${getToken()}`}, body: JSON.stringify(data) }).then(r => r.blob()),
    debSummary:      (cid, m, y)     => req('GET',    `/documents-multilingues/export-docs/deb-summary${qs({ company_id: cid, month: m, year: y })}`),
  },

  // ── Superadmin ─────────────────────────────────────────────────
  superadmin: {
    login:            (data)         => fetch(`${BASE}/superadmin/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(r => r.json()),
    logout:           (tok)          => fetch(`${BASE}/superadmin/logout`, { method: 'POST', headers: { Authorization: `Bearer ${tok}` } }).then(r => r.json()),
    setup2fa:         (tok)          => saReq(tok, 'POST', '/superadmin/2fa/setup'),
    dashboard:        (tok)          => saReq(tok, 'GET',  '/superadmin/dashboard'),
    logs:             (tok, p={})    => saReq(tok, 'GET',  `/superadmin/logs${qs(p)}`),
    tenants:          (tok, p={})    => saReq(tok, 'GET',  `/superadmin/tenants${qs(p)}`),
    tenant:           (tok, id)      => saReq(tok, 'GET',  `/superadmin/tenants/${id}`),
    createTenant:     (tok, data)    => saReq(tok, 'POST', '/superadmin/tenants', data),
    updateTenant:     (tok, id, d)   => saReq(tok, 'PUT',  `/superadmin/tenants/${id}`, d),
    deleteTenant:     (tok, id)      => saReq(tok, 'DELETE',`/superadmin/tenants/${id}`),
    suspendTenant:    (tok, id, d)   => saReq(tok, 'POST', `/superadmin/tenants/${id}/suspend`, d),
    reactivateTenant: (tok, id)      => saReq(tok, 'POST', `/superadmin/tenants/${id}/reactivate`),
    extendTrial:      (tok, id, d)   => saReq(tok, 'POST', `/superadmin/tenants/${id}/extend-trial`, d),
    giftMonth:        (tok, id)      => saReq(tok, 'POST', `/superadmin/tenants/${id}/gift-month`),
    resetPassword:    (tok, id, d)   => saReq(tok, 'POST', `/superadmin/tenants/${id}/reset-password`, d),
    impersonate:      (tok, id, d)   => saReq(tok, 'POST', `/superadmin/tenants/${id}/impersonate`, d),
    demoLinks:        (tok)          => saReq(tok, 'GET',  '/superadmin/demo-links'),
    createDemoLink:   (tok, d)       => saReq(tok, 'POST', '/superadmin/demo-links', d),
    revokeDemoLink:   (tok, id)      => saReq(tok, 'POST', `/superadmin/demo-links/${id}/revoke`),
    tickets:          (tok, p={})    => saReq(tok, 'GET',  `/superadmin/support/tickets${qs(p)}`),
    ticket:           (tok, id)      => saReq(tok, 'GET',  `/superadmin/support/tickets/${id}`),
    createTicket:     (tok, data)    => saReq(tok, 'POST', '/superadmin/support/tickets', data),
    updateTicket:     (tok, id, d)   => saReq(tok, 'PUT',  `/superadmin/support/tickets/${id}`, d),
    replyTicket:      (tok, id, d)   => saReq(tok, 'POST', `/superadmin/support/tickets/${id}/messages`, d),
    ticketStats:      (tok)          => saReq(tok, 'GET',  '/superadmin/support/stats'),
    flags:            (tok, tid)     => saReq(tok, 'GET',  `/superadmin/modules/flags${qs({ tenant_id: tid })}`),
    setFlag:          (tok, data)    => saReq(tok, 'POST', '/superadmin/modules/flags', data),
    monitoring:       (tok)          => saReq(tok, 'GET',  '/superadmin/monitoring'),
    communications:   (tok)          => saReq(tok, 'GET',  '/superadmin/communications'),
    sendComm:         (tok, data)    => saReq(tok, 'POST', '/superadmin/communications', data),
    deleteComm:       (tok, id)      => saReq(tok, 'DELETE',`/superadmin/communications/${id}`),
    subscriptions:    (tok, p={})    => saReq(tok, 'GET',  `/superadmin/subscriptions${qs(p)}`),
  },
  swan: {
    // Onboarding
    onboarding:         ()              => req('GET',   '/swan/onboarding'),
    startOnboarding:    (d)             => req('POST',  '/swan/onboarding', d),
    // Accounts
    accounts:           ()              => req('GET',   '/swan/accounts'),
    syncAccounts:       ()              => req('POST',  '/swan/accounts/sync'),
    // Transactions
    transactions:       (id, p={})      => req('GET',   `/swan/accounts/${id}/transactions${qs(p)}`),
    syncTransactions:   (id)            => req('POST',  `/swan/accounts/${id}/transactions/sync`),
    reconcile:          (txId, d)       => req('POST',  `/swan/transactions/${txId}/reconcile`, d),
    autoReconcile:      (d)             => req('POST',  '/swan/reconcile/auto', d),
    // Payments
    payments:           (id)            => req('GET',   `/swan/accounts/${id}/payments`),
    createPayment:      (id, d)         => req('POST',  `/swan/accounts/${id}/payments`, d),
    bulkPayments:       (id, d)         => req('POST',  `/swan/accounts/${id}/payments/bulk`, d),
    // Cards
    cards:              (id)            => req('GET',   `/swan/accounts/${id}/cards`),
    createCard:         (id, d)         => req('POST',  `/swan/accounts/${id}/cards`, d),
    updateCard:         (id, cardId, d) => req('PATCH', `/swan/accounts/${id}/cards/${cardId}`, d),
    // Mandates
    mandates:           (id)            => req('GET',   `/swan/accounts/${id}/mandates`),
    // Stats
    stats:              (id)            => req('GET',   `/swan/accounts/${id}/stats`),
    // RIB
    rib:                (id)            => req('GET',   `/swan/accounts/${id}/rib`),
  },
  ged: {
    upload:         (file)          => { const fd = new FormData(); fd.append('file', file); return reqUpload('/ged/upload', fd); },
    stats:          (p={})         => req('GET',    `/ged/stats${qs(p)}`),
    categories:     (p={})         => req('GET',    `/ged/categories${qs(p)}`),
    documents:      (p={})         => req('GET',    `/ged/documents${qs(p)}`),
    document:       (id)           => req('GET',    `/ged/documents/${id}`),
    createDocument: (d)            => req('POST',   '/ged/documents', d),
    updateDocument: (id, d)        => req('PUT',    `/ged/documents/${id}`, d),
    deleteDocument: (id)           => req('DELETE', `/ged/documents/${id}`),
    analyze:        (id)           => req('POST',   `/ged/documents/${id}/analyze`),
    addLink:        (id, d)        => req('POST',   `/ged/documents/${id}/links`, d),
    removeLink:     (id, lid)      => req('DELETE', `/ged/documents/${id}/links/${lid}`),
    addReminder:    (id, d)        => req('POST',   `/ged/documents/${id}/reminders`, d),
    expiryCalendar: (p={})         => req('GET',    `/ged/expiry-calendar${qs(p)}`),
    search:         (p={})         => req('GET',    `/ged/search${qs(p)}`),
    agents:         (p={})         => req('GET',    `/ged/agents${qs(p)}`),
    agent:          (id)           => req('GET',    `/ged/agents/${id}`),
    createAgent:    (d)            => req('POST',   '/ged/agents', d),
    updateAgent:    (id, d)        => req('PUT',    `/ged/agents/${id}`, d),
    deleteAgent:    (id)           => req('DELETE', `/ged/agents/${id}`),
  },
  veille: {
    news:                (p={})        => req('GET',   `/veille/news${qs(p)}`),
    getNews:             (id)          => req('GET',   `/veille/news/${id}`),
    createNews:          (d)           => req('POST',  '/veille/news', d),
    updateNews:          (id, d)       => req('PUT',   `/veille/news/${id}`, d),
    deleteNews:          (id)          => req('DELETE',`/veille/news/${id}`),
    markRead:            (id, read=true)=>req('PATCH', `/veille/news/${id}/read`, { read }),
    toggleBookmark:      (id)          => req('PATCH', `/veille/news/${id}/bookmark`),
    analyzeNews:         (id, d)       => req('POST',  `/veille/news/${id}/analyze`, d),
    sources:             ()            => req('GET',   '/veille/sources'),
    createSource:        (d)           => req('POST',  '/veille/sources', d),
    updateSource:        (id, d)       => req('PUT',   `/veille/sources/${id}`, d),
    deleteSource:        (id)          => req('DELETE',`/veille/sources/${id}`),
    toggleSource:        (id)          => req('PATCH', `/veille/sources/${id}/toggle`),
    testSource:          (id)          => req('POST',  `/veille/sources/${id}/test`),
    fetch:               ()            => req('POST',  '/veille/fetch'),
    stats:               ()            => req('GET',   '/veille/stats'),
    bookmarks:           ()            => req('GET',   '/veille/bookmarks'),
    updateBookmarkNote:  (id, note)    => req('PATCH', `/veille/bookmarks/${id}/note`, { note }),
    deadlines:           ()            => req('GET',   '/veille/deadlines'),
  },

  // ── Champs personnalisés ──────────────────────────────────────
  customFields: {
    list:       (p = {})           => req('GET',    `/custom-fields${qs(p)}`),
    create:     (data)             => req('POST',   '/custom-fields', data),
    update:     (id, data)         => req('PUT',    `/custom-fields/${id}`, data),
    delete:     (id)               => req('DELETE', `/custom-fields/${id}`),
    reorder:    (items)            => req('POST',   '/custom-fields/reorder', { items }),
    getValues:  (entity_type, entity_id) => req('GET', `/custom-fields/values${qs({ entity_type, entity_id })}`),
    saveValues: (data)             => req('POST',   '/custom-fields/values', data),
  },

  // ── Échéancier de paiement ────────────────────────────────────
  paymentSchedule: {
    get:      (invoiceId)                  => req('GET',  `/invoices/${invoiceId}/payment-schedule`),
    set:      (invoiceId, items)           => req('POST', `/invoices/${invoiceId}/payment-schedule`, items),
    update:   (invoiceId, schedId, data)   => req('PUT',  `/invoices/${invoiceId}/payment-schedule/${schedId}`, data),
    pay:      (invoiceId, schedId, data)   => req('POST', `/invoices/${invoiceId}/payment-schedule/${schedId}/pay`, data),
    upcoming: (p = {})                     => req('GET',  `/invoices/schedules/upcoming${qs(p)}`),
  },

  // ── Agrégation bancaire Bridge (DSP2) ────────────────────────
  banking: {
    banks:          ()               => req('GET',    '/banking/banks'),
    connections:    (p = {})         => req('GET',    `/banking/connections${qs(p)}`),
    connection:     (id)             => req('GET',    `/banking/connections/${id}`),
    disconnect:     (id)             => req('DELETE', `/banking/connections/${id}`),
    initiate:       (p = {})         => req('GET',    `/banking/connect/initiate${qs(p)}`),
    accounts:       (p = {})         => req('GET',    `/banking/accounts${qs(p)}`),
    accountTxs:     (id, p = {})     => req('GET',    `/banking/accounts/${id}/transactions${qs(p)}`),
    transactions:   (p = {})         => req('GET',    `/banking/transactions${qs(p)}`),
    sync:           (connId)         => req('POST',   `/banking/sync/${connId}`),
    reconcile:      (txId, data)     => req('POST',   `/banking/reconcile/${txId}`, data),
    autoReconcile:  ()               => req('POST',   '/banking/reconcile/auto'),
    summary:        ()               => req('GET',    '/banking/summary'),
    balanceHistory: ()               => req('GET',    '/banking/balance-history'),
  },
  stripeConnect: {
    status:   (cid) => req('GET',  `/stripe-connect/status${qs({ company_id: cid })}`),
    initiate: (cid) => req('GET',  `/stripe-connect/oauth/initiate${qs({ company_id: cid })}`),
    disconnect: (cid) => req('DELETE', `/stripe-connect/${cid}`),
  },
  ai: {
    quickActions: (mod)    => req('GET',    '/ai/quick-actions?module=' + (mod||'general')),
    conversations:()       => req('GET',    '/ai/conversations'),
    conversation: (id)     => req('GET',    '/ai/conversations/' + id),
    usage:        ()       => req('GET',    '/ai/usage'),
  },
  billing: {
    myPlan:      ()       => req('GET',  '/billing/my-plan'),
    getPlans:    ()       => req('GET',  '/billing/plans'),
    estimate:    ()       => req('GET',  '/billing/estimate'),
    saveConfig:  (data)   => req('PUT',  '/billing/config', data),
    createCheckoutSession: (data) => req('POST', '/billing/checkout-session', data),
    changePlan:  (data)   => req('POST', '/billing/plan/change', data),
    confirmRenewal: () => req('POST', '/billing/renewal/confirm'),
  },
  // ── Gestion d'équipe (invitations, rôles, sièges) ────────────
  team: {
    members:         ()               => req('GET',   '/team/members'),
    invite:          (data)           => req('POST',  '/team/invite', data),
    updateRole:      (id, role)       => req('PATCH', `/team/members/${id}/role`, { role }),
    deactivate:      (id)             => req('PATCH', `/team/members/${id}/deactivate`),
    reactivate:      (id)             => req('PATCH', `/team/members/${id}/reactivate`),
    getPermissions:  (id)             => req('GET',   `/team/members/${id}/permissions`),
    setPermission:   (id, key, granted) => req('PATCH', `/team/members/${id}/permissions`, { permission_key: key, granted }),
  },
  invoiceArchive: {
    extract: (data) => req('POST', '/invoice-archive/extract', data),
  },
};

function saReq(token, method, path, body) {
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json());
}

export default api;
